import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { PackageSearch, Camera, X, CheckCircle, AlertCircle, Bell, Clock } from 'lucide-react';
import axios from 'axios';
import QrScanner from 'qr-scanner';
import { useTranslation } from 'react-i18next';

/** Maps backend order.status to progress step index (0-based) */
const statusToProgressIndex = (status) => {
  switch (status) {
    case 'pending':
    case 'confirmed':
      return 0;
    case 'in_production':
    case 'manufacturing':
      return 1;
    case 'quality_check':
      return 2;
    case 'ready_for_pickup':
    case 'out_for_delivery':
      return 3;
    case 'back_to_central':
    case 'revoked_to_central':
      return 3;
    case 'delivered':
      return 4;
    case 'completed':
      return 5;
    case 'cancelled':
      return -1;
    default:
      return 0;
  }
};

const TRACKING_STEPS = [
  {
    id: 'waiting_room',
    title: 'Order received',
    subtitle: 'Preparing before lab',
    slaMinutes: 60,
    slaLabel: 'Typical time in this step: up to 1 hour'
  },
  {
    id: 'lab_working',
    title: 'Being worked on',
    subtitle: 'At the lab'
  },
  {
    id: 'out_of_lab',
    title: 'Out of lab',
    subtitle: 'Work finished at lab'
  },
  {
    id: 'not_delivered',
    title: 'Not delivered yet',
    subtitle: 'Ready for pickup or on the way'
  },
  {
    id: 'delivered',
    title: 'Delivered',
    subtitle: 'Handed to customer'
  },
  {
    id: 'client_confirmed',
    title: 'After sale',
    subtitle: 'Order completed'
  }
];

const ALTERNATIVE_ROUTE_STEPS = [
  {
    id: 'monthly_calls',
    title: 'Monthly client calls',
    subtitle: 'Sales reminder once each month for 3 months'
  },
  {
    id: 'back_to_central',
    title: 'Back to central',
    subtitle: 'After 3 months without delivery'
  },
  {
    id: 'revoked_to_central',
    title: 'Revoked to central',
    subtitle: 'After 6 months equipment belongs to central'
  }
];

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Subtitle = styled.p`
  color: #64748b;
  font-size: 0.9375rem;
  margin: 0;
`;

const SearchCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.9375rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.variant === 'secondary' ? '#f3f4f6' : '#667eea'};
  color: ${props => props.variant === 'secondary' ? '#1e293b' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: ${props => props.variant === 'secondary' ? '#e5e7eb' : '#5568d3'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ScannerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  background: #f1f5f9;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const ScannerVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const ScannerOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 250px;
  height: 250px;
  border: 3px solid #667eea;
  border-radius: 12px;
  background: transparent;
  pointer-events: none;
  z-index: 3;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
`;

const OrderCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const OrderTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
`;

const StatusBadge = styled.div`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => {
    switch(props.status) {
      case 'completed': return '#d1fae5';
      case 'delivered': return '#dbeafe';
      case 'back_to_central': return '#ffedd5';
      case 'revoked_to_central': return '#fee2e2';
      case 'cancelled': return '#fee2e2';
      default: return '#fef3c7';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'completed': return '#065f46';
      case 'delivered': return '#1e40af';
      case 'back_to_central': return '#9a3412';
      case 'revoked_to_central': return '#991b1b';
      case 'cancelled': return '#991b1b';
      default: return '#92400e';
    }
  }};
  text-transform: capitalize;
`;

const OrderInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  
  .label {
    font-size: 0.75rem;
    color: #64748b;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .value {
    font-size: 0.9375rem;
    color: #1e293b;
    font-weight: 500;
  }
`;

const WorkflowSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
`;

const WorkflowTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const WorkflowItem = styled.div`
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  
  .status {
    font-weight: 600;
    color: #667eea;
    margin-bottom: 0.25rem;
  }
  
  .notes {
    color: #64748b;
  }
  
  .meta {
    font-size: 0.75rem;
    color: #94a3b8;
    margin-top: 0.25rem;
  }
`;

const ProgressSection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.25rem 1rem;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;

const ProgressHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const AlertPlaceholder = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 0.8125rem;
  color: #1e40af;
  line-height: 1.45;
  margin-bottom: 1rem;

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
`;

const OverdueDeliveryNote = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  background: ${(p) => p.$critical ? '#fef2f2' : '#fff7ed'};
  border: 1px solid ${(p) => p.$critical ? '#fecaca' : '#fed7aa'};
  border-radius: 10px;
  color: ${(p) => p.$critical ? '#991b1b' : '#9a3412'};
  font-size: 0.85rem;
  line-height: 1.45;
  margin-bottom: 1rem;

  .title {
    font-weight: 800;
    margin-bottom: 0.2rem;
  }

  svg {
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
`;

const LensStatusCard = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1rem;
  margin-bottom: 1rem;
  background: ${(p) => p.$urgent ? '#fffbeb' : '#f0fdf4'};
  border: 1px solid ${(p) => p.$urgent ? '#fde68a' : '#bbf7d0'};
  border-radius: 12px;
  color: ${(p) => p.$urgent ? '#92400e' : '#166534'};

  .title {
    font-weight: 800;
    margin-bottom: 0.2rem;
  }

  .message {
    font-size: 0.875rem;
    line-height: 1.45;
  }
`;

const ProgressScroll = styled.div`
  overflow-x: auto;
  padding: 0.25rem 0 0.5rem;
  margin: 0 -0.25rem;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: stretch;
  min-width: min(960px, 100%);
  gap: 0;
`;

const BranchProgress = styled.div`
  min-width: min(960px, 100%);
  padding-top: 0.75rem;
`;

const BranchStartRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(92px, 1fr));
  align-items: start;
  min-height: 58px;
`;

const BranchDrop = styled.div`
  grid-column: 4 / 5;
  justify-self: center;
  width: 2px;
  height: 52px;
  background: ${(p) => p.$active ? '#ef4444' : '#e2e8f0'};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${(p) => p.$active ? '#ef4444' : '#e2e8f0'};
    transform: translateX(-50%);
  }
`;

const BranchRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(92px, 1fr));
  align-items: start;
`;

const BranchStepWrap = styled.div`
  grid-column: ${(p) => p.$column};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ProgressStepWrap = styled.div`
  flex: 1;
  min-width: 92px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
`;

const StepDotRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 0.5rem;
`;

const StepDot = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(p) => (p.$done ? '#10b981' : p.$active ? '#667eea' : '#e2e8f0')};
  color: ${(p) => (p.$done || p.$active ? '#fff' : '#94a3b8')};
  border: 2px solid ${(p) => (p.$active ? '#c7d2fe' : p.$done ? '#6ee7b7' : '#f1f5f9')};
  box-shadow: ${(p) =>
    p.$active ? '0 0 0 4px rgba(102, 126, 234, 0.18)' : 'none'};
  font-size: 0.65rem;
  font-weight: 700;
  z-index: 1;
`;

const BranchStepDot = styled(StepDot)`
  background: ${(p) => (p.$done || p.$active ? '#ef4444' : '#e5e7eb')};
  color: ${(p) => (p.$done || p.$active ? '#fff' : '#94a3b8')};
  border-color: ${(p) => (p.$done || p.$active ? '#fecaca' : '#f1f5f9')};
  box-shadow: ${(p) => (p.$active ? '0 0 0 4px rgba(239, 68, 68, 0.16)' : 'none')};
`;

const StepLine = styled.div`
  flex: 1;
  height: 3px;
  min-width: 4px;
  background: ${(p) => (p.$filled ? '#10b981' : '#e2e8f0')};
  border-radius: 2px;
`;

const BranchStepLine = styled(StepLine)`
  background: ${(p) => (p.$filled ? '#ef4444' : '#e2e8f0')};
`;

const StepEndSpacer = styled.div`
  flex: 1;
  min-width: 4px;
`;

const StepTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${(p) => (p.$muted ? '#94a3b8' : '#1e293b')};
  line-height: 1.25;
  max-width: 104px;
`;

const StepSubtitle = styled.div`
  font-size: 0.62rem;
  color: #94a3b8;
  margin-top: 0.15rem;
  line-height: 1.2;
  max-width: 104px;
`;

const CancelledBanner = styled.div`
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

function getReadyForPickupDate(order) {
  if (order?.firstReadyForPickupAt) return new Date(order.firstReadyForPickupAt);
  const entry = (order?.workflow || []).find((item) =>
    item?.status === 'received_at_store' || item?.status === 'ready_for_pickup'
  );
  return entry?.timestamp ? new Date(entry.timestamp) : null;
}

function getOverdueDeliveryState(order) {
  if (!['ready_for_pickup', 'out_for_delivery', 'back_to_central', 'revoked_to_central'].includes(order?.status)) {
    return null;
  }
  const readyAt = getReadyForPickupDate(order);
  if (!readyAt || Number.isNaN(readyAt.getTime())) return null;
  const months = Math.max(0, Math.floor((Date.now() - readyAt.getTime()) / (30 * 24 * 60 * 60 * 1000)));
  return {
    months,
    reminderCount: Math.min(3, Math.max(months, order.clientCallReminderMonths?.length || 0)),
    backToCentral: order.status === 'back_to_central' || order.status === 'revoked_to_central' || months >= 3,
    revoked: order.status === 'revoked_to_central' || months >= 6,
  };
}

function OrderTrackingProgress({ order }) {
  const { t } = useTranslation();
  const { status } = order || {};
  const overdueState = getOverdueDeliveryState(order);
  const steps = TRACKING_STEPS;
  const idx = statusToProgressIndex(status);
  const isCancelled = idx < 0;
  const fullyDone = status === 'completed';
  const alternativeIdx = status === 'revoked_to_central'
    ? 2
    : status === 'back_to_central'
      ? 1
      : overdueState?.months >= 1
        ? 0
        : -1;

  if (isCancelled) {
    return <CancelledBanner>{t('trackOrder.thisOrderWasCancelledThe')}</CancelledBanner>;
  }

  return (
    <ProgressSection>
      <ProgressHeading>
        <CheckCircle size={18} color="#667eea" />
        {t('trackOrder.orderProgress')}
      </ProgressHeading>

      <AlertPlaceholder>
        <Bell size={18} />
        <span>
          <strong>{t('trackOrder.deliveryReminders')}</strong> {t('trackOrder.ifAnOrderIsReady')}
        </span>
      </AlertPlaceholder>

      {overdueState && overdueState.months >= 1 && !overdueState.revoked && (
        <OverdueDeliveryNote>
          <Bell size={18} />
          <div>
            <div className="title">{t('trackOrder.clientCallFollowUpActive')}</div>
            {t('trackOrder.salesHasBeenReminded')} {overdueState.reminderCount}{t('trackOrder.x3MonthlyTime')}{overdueState.reminderCount === 1 ? '' : 's'} {t('trackOrder.toCallTheClient')}
            {overdueState.backToCentral ? t('trackOrder.thisOrderShouldNowBe') : t('trackOrder.ifItIsStillNot')}
          </div>
        </OverdueDeliveryNote>
      )}

      {overdueState?.revoked && (
        <OverdueDeliveryNote $critical>
          <AlertCircle size={18} />
          <div>
            <div className="title">{t('trackOrder.orderRevokedToCentral')}</div>
            {t('trackOrder.thisOrderWasNotDelivered')}
          </div>
        </OverdueDeliveryNote>
      )}

      <ProgressScroll>
        <ProgressRow>
          {steps.map((step, i) => {
            const done = i < idx || (fullyDone && i <= idx);
            const active = i === idx && !fullyDone;
            const n = steps.length;
            const lineToNextFilled = fullyDone ? true : idx > i;
            const lineFromPrevFilled = fullyDone ? true : idx >= i;
            return (
              <ProgressStepWrap key={step.id}>
                <StepDotRow>
                  {i > 0 ? <StepLine $filled={lineFromPrevFilled} /> : <StepEndSpacer />}
                  <StepDot $done={done} $active={active}>
                    {done ? <CheckCircle size={14} strokeWidth={2.5} /> : i + 1}
                  </StepDot>
                  {i < n - 1 ? <StepLine $filled={lineToNextFilled} /> : <StepEndSpacer />}
                </StepDotRow>
                <StepTitle $muted={!done && !active}>{step.title}</StepTitle>
                <StepSubtitle>{step.subtitle}</StepSubtitle>
              </ProgressStepWrap>
            );
          })}
        </ProgressRow>

        <BranchProgress>
          <BranchStartRow>
            <BranchDrop $active={alternativeIdx >= 0} />
          </BranchStartRow>
          <BranchRow>
            {ALTERNATIVE_ROUTE_STEPS.map((step, i) => {
              const done = i < alternativeIdx;
              const active = i === alternativeIdx;
              const columns = ['4 / 5', '5 / 6', '6 / 7'];
              return (
                <BranchStepWrap key={step.id} $column={columns[i]}>
                  <StepDotRow>
                    {i > 0 ? <BranchStepLine $filled={alternativeIdx >= i} /> : <StepEndSpacer />}
                    <BranchStepDot $done={done} $active={active}>
                      {done ? <CheckCircle size={14} strokeWidth={2.5} /> : i + 1}
                    </BranchStepDot>
                    {i < ALTERNATIVE_ROUTE_STEPS.length - 1 ? <BranchStepLine $filled={alternativeIdx > i} /> : <StepEndSpacer />}
                  </StepDotRow>
                  <StepTitle $muted={!done && !active}>{step.title}</StepTitle>
                  <StepSubtitle>{step.subtitle}</StepSubtitle>
                </BranchStepWrap>
              );
            })}
          </BranchRow>
        </BranchProgress>
      </ProgressScroll>

    </ProgressSection>
  );
}

const LENS_TRACKING_COPY = {
  reserved_waiting_inventory: {
    title: 'Waiting for inventory to prepare lens',
    message: 'The lens is reserved from stock. Central inventory is preparing it before lab handoff.',
    urgent: true
  },
  lens_ready_waiting_frame: {
    title: 'Lens ready, waiting for frame',
    message: 'The lens is ready. We are waiting for the frame/package to reach central before equipment goes to the lab.',
    urgent: false
  },
  supplier_order_required: {
    title: 'Waiting for supplier order',
    message: 'The lens is not currently in stock. Central inventory needs to order it from the supplier.',
    urgent: true
  },
  lens_ordered_waiting_supplier: {
    title: 'Waiting for supplier to deliver lens',
    message: 'The supplier order has been placed. The order will move forward once the lens arrives.',
    urgent: true
  },
  lens_arrived_waiting_frame: {
    title: 'Lens arrived, waiting for frame to arrive at central',
    message: 'The supplier lens has arrived. We are waiting for the store frame package to reach central and be opened before lab handoff.',
    urgent: true
  },
  lens_arrived_equipment_ready: {
    title: 'Equipment ready to go to lab',
    message: 'The lens and frame are both at central and the package has been opened. Equipment is ready for lab handoff.',
    urgent: false
  }
};

function LensFulfillmentStatus({ lensFulfillment }) {
  const status = lensFulfillment?.status;
  const copy = LENS_TRACKING_COPY[status];
  if (!copy) return null;
  return (
    <LensStatusCard $urgent={copy.urgent}>
      {copy.urgent ? <Clock size={18} /> : <CheckCircle size={18} />}
      <div>
        <div className="title">{copy.title}</div>
        <div className="message">{copy.message}</div>
      </div>
    </LensStatusCard>
  );
}

const TrackOrderPage = () => {
  const { t } = useTranslation();
  const [orderNumber, setOrderNumber] = useState('');
  const [trackedOrder, setTrackedOrder] = useState(null);
  const [trackingError, setTrackingError] = useState('');
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const qrScannerRef = useRef(null);
  const fallbackScanTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const extractOrderNumberFromQr = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';

    // Accept our normal QR value (just the order number), URLs like
    // /track-order?order=ORD-..., or older QR payloads containing JSON.
    try {
      const json = JSON.parse(raw);
      if (json?.orderNumber) return String(json.orderNumber).trim();
      if (json?.code) return String(json.code).trim();
    } catch (_) {}

    try {
      const url = new URL(raw, window.location.origin);
      const fromQuery = url.searchParams.get('order') || url.searchParams.get('orderNumber') || url.searchParams.get('code');
      if (fromQuery) return fromQuery.trim();
      const pathMatch = decodeURIComponent(url.pathname).match(/ORD-[A-Z0-9-]+/i);
      if (pathMatch) return pathMatch[0].trim();
    } catch (_) {}

    const inlineMatch = raw.match(/ORD-[A-Z0-9-]+/i);
    return (inlineMatch ? inlineMatch[0] : raw).trim();
  };

  const processScannedQrText = (value) => {
    const scannedOrderNumber = extractOrderNumberFromQr(value);
    if (!scannedOrderNumber) return false;
    setOrderNumber(scannedOrderNumber);
    stopScanner();
    handleTrackOrderFromScan(scannedOrderNumber);
    return true;
  };

  const startFallbackFrameScanner = () => {
    if (fallbackScanTimerRef.current) clearInterval(fallbackScanTimerRef.current);
    fallbackScanTimerRef.current = window.setInterval(async () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2) return;
      try {
        const result = await QrScanner.scanImage(video, {
          returnDetailedScanResult: true,
          alsoTryWithoutScanRegion: true,
        });
        const text = typeof result === 'string' ? result : result?.data;
        if (text) processScannedQrText(text);
      } catch (_) {
        // No QR found in this frame. Keep scanning silently.
      }
    }, 350);
  };

  const handleTrackOrder = async (e) => {
    e?.preventDefault();
    if (!orderNumber.trim()) {
      setTrackingError('Please enter an order number');
      return;
    }

    setTrackingLoading(true);
    setTrackingError('');
    setTrackedOrder(null);

    try {
      const response = await axios.get(`/api/orders/track/${orderNumber.trim()}`);
      if (response.data.success) {
        setTrackedOrder(response.data.order);
      } else {
        setTrackingError(response.data.message || 'Order not found');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setTrackingError('Order not found. Please check your order number.');
      } else {
        setTrackingError('Error tracking order. Please try again.');
      }
    } finally {
      setTrackingLoading(false);
    }
  };

  const startScanner = async () => {
    try {
      setTrackingError('');
      
      if (qrScannerRef.current) {
        qrScannerRef.current.stop();
        qrScannerRef.current.destroy();
        qrScannerRef.current = null;
      }
      
      setIsScanning(true);
      await new Promise(resolve => setTimeout(resolve, 300));

      let retries = 0;
      while (!videoRef.current && retries < 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!videoRef.current) {
        setTrackingError('Video element not available. Please refresh the page and try again.');
        setIsScanning(false);
        return;
      }

      videoRef.current.style.display = 'block';
      videoRef.current.style.visibility = 'visible';
      videoRef.current.style.width = '100%';
      videoRef.current.style.height = '100%';
      videoRef.current.style.objectFit = 'cover';
      videoRef.current.setAttribute('playsinline', 'true');
      videoRef.current.setAttribute('muted', 'true');
      videoRef.current.setAttribute('autoplay', 'true');

      let qrScanner = new QrScanner(
        videoRef.current,
        (result) => {
          const scannedText = typeof result === 'string' ? result : result?.data;
          if (scannedText) {
            processScannedQrText(scannedText);
          }
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          maxScansPerSecond: 12,
          preferredCamera: 'environment',
          calculateScanRegion: (video) => {
            const smallerDimension = Math.min(video.videoWidth || video.clientWidth, video.videoHeight || video.clientHeight);
            const scanRegionSize = Math.round(smallerDimension * 0.82);
            return {
              x: Math.round(((video.videoWidth || video.clientWidth) - scanRegionSize) / 2),
              y: Math.round(((video.videoHeight || video.clientHeight) - scanRegionSize) / 2),
              width: scanRegionSize,
              height: scanRegionSize,
              downScaledWidth: 500,
              downScaledHeight: 500,
            };
          },
        }
      );

      qrScannerRef.current = qrScanner;
      await qrScanner.start();
      if (videoRef.current?.srcObject) {
        streamRef.current = videoRef.current.srcObject;
      }
      startFallbackFrameScanner();
    } catch (error) {
      console.error('Scanner error:', error);
      setIsScanning(false);
      
      if (error.name === 'NotAllowedError') {
        setTrackingError('Camera permission denied. Please allow camera access and try again.');
      } else if (error.name === 'NotFoundError') {
        setTrackingError('No camera found. Please use manual entry.');
      } else if (error.name === 'NotReadableError') {
        setTrackingError('Camera is already in use. Please close other apps using the camera.');
      } else {
        setTrackingError('Failed to start camera. Please check permissions or try manual entry.');
      }
    }
  };

  const stopScanner = () => {
    if (fallbackScanTimerRef.current) {
      clearInterval(fallbackScanTimerRef.current);
      fallbackScanTimerRef.current = null;
    }
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.style.display = 'none';
      videoRef.current.style.visibility = 'hidden';
    }
    setIsScanning(false);
  };

  const handleTrackOrderFromScan = async (scannedOrderNumber) => {
    const cleanOrderNumber = extractOrderNumberFromQr(scannedOrderNumber);
    if (!cleanOrderNumber.trim()) {
      setTrackingError('Invalid order number scanned');
      return;
    }

    setTrackingLoading(true);
    setTrackingError('');
    setTrackedOrder(null);

    try {
      const response = await axios.get(`/api/orders/track/${encodeURIComponent(cleanOrderNumber.trim())}`);
      if (response.data.success) {
        setTrackedOrder(response.data.order);
      } else {
        setTrackingError(response.data.message || 'Order not found');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setTrackingError('Order not found. Please check your order number.');
      } else {
        setTrackingError('Error tracking order. Please try again.');
      }
    } finally {
      setTrackingLoading(false);
    }
  };

  const clearTracking = () => {
    setOrderNumber('');
    setTrackedOrder(null);
    setTrackingError('');
    stopScanner();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  return (
    <PageContainer>
      <HeaderCard>
        <Title>
          <PackageSearch size={24} />
          {t('trackOrder.title')}
        </Title>
        <Subtitle>
          {t('trackOrder.subtitle')}
        </Subtitle>
      </HeaderCard>

      <SearchCard>
        <InputGroup>
          <Input
            type="text"
            placeholder={t('trackOrder.placeholder')}
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleTrackOrder()}
          />
          <Button onClick={handleTrackOrder} disabled={trackingLoading || !orderNumber.trim()}>
            <PackageSearch size={18} />
            {trackingLoading ? t('trackOrder.tracking') : t('trackOrder.track')}
          </Button>
          {trackedOrder && (
            <Button variant="secondary" onClick={clearTracking}>
              <X size={18} />
              {t('trackOrder.clear')}
            </Button>
          )}
        </InputGroup>

        {!isScanning && (
          <Button
            variant="secondary"
            onClick={startScanner}
            style={{ width: '100%', marginTop: '0.5rem' }}
          >
            <Camera size={18} />
            {t('trackOrder.scanQr')}
          </Button>
        )}

        {isScanning && (
          <ScannerContainer>
            <ScannerVideo ref={videoRef} />
            <ScannerOverlay />
            <Button
              variant="secondary"
              onClick={stopScanner}
              style={{ position: 'relative', zIndex: 4, marginTop: 'auto', marginBottom: '1rem' }}
            >
              <X size={18} />
              {t('trackOrder.stopScanner')}
            </Button>
          </ScannerContainer>
        )}

        {trackingError && (
          <ErrorMessage>
            <AlertCircle size={18} />
            {trackingError}
          </ErrorMessage>
        )}
      </SearchCard>

      {trackedOrder && (
        <OrderCard>
          <OrderHeader>
            <OrderTitle>{t('trackOrder.order')} {trackedOrder.orderNumber}</OrderTitle>
            <StatusBadge status={trackedOrder.status}>
              {trackedOrder.status}
            </StatusBadge>
          </OrderHeader>

          <OrderTrackingProgress
            order={trackedOrder}
          />

          <LensFulfillmentStatus lensFulfillment={trackedOrder.lensFulfillment} />

          <OrderInfo>
            <InfoItem>
              <span className="label">{t('trackOrder.orderNumber')}</span>
              <span className="value">{trackedOrder.orderNumber}</span>
            </InfoItem>
            <InfoItem>
              <span className="label">{t('trackOrder.status')}</span>
              <span className="value">{trackedOrder.status}</span>
            </InfoItem>
            <InfoItem>
              <span className="label">{t('trackOrder.totalAmount')}</span>
              <span className="value">
                {trackedOrder.total != null
                  ? `${Number(trackedOrder.total).toLocaleString()} DZD`
                  : t('trackOrder.nA')}
              </span>
            </InfoItem>
            <InfoItem>
              <span className="label">{t('trackOrder.createdDate')}</span>
              <span className="value">{formatDate(trackedOrder.createdAt)}</span>
            </InfoItem>
            {trackedOrder.visitor && (
              <InfoItem>
                <span className="label">{t('trackOrder.visitor')}</span>
                <span className="value">
                  {trackedOrder.visitor.gender} - {trackedOrder.visitor.ageGroup}
                </span>
              </InfoItem>
            )}
            {trackedOrder.store && (
              <InfoItem>
                <span className="label">{t('trackOrder.store')}</span>
                <span className="value">
                  {trackedOrder.store.name || trackedOrder.store.storeCode || 'N/A'}
                </span>
              </InfoItem>
            )}
          </OrderInfo>

          {trackedOrder.workflow && trackedOrder.workflow.length > 0 && (
            <WorkflowSection>
              <WorkflowTitle>{t('trackOrder.orderTimeline')}</WorkflowTitle>
              {trackedOrder.workflow.map((entry, index) => (
                <WorkflowItem key={index}>
                  <div className="status">{entry.status}</div>
                  {entry.notes && <div className="notes">{entry.notes}</div>}
                  <div className="meta">
                    {entry.location && `${entry.location} • `}
                    {formatDate(entry.timestamp || entry.createdAt)}
                  </div>
                </WorkflowItem>
              ))}
            </WorkflowSection>
          )}
        </OrderCard>
      )}
    </PageContainer>
  );
};

export default TrackOrderPage;

